import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useCompanyData } from '../hooks/useCompanyData';
import type { Database } from '../types/database.types';

type ProjectFormData = Database['public']['Tables']['projects']['Insert'];

export default function ProjectManagement() {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ProjectFormData>();
  const queryClient = useQueryClient();

  // Roep de nieuwe RPC functie aan om de company_id direct op te halen
const { data: companyId, isLoading: isLoadingUser } = useQuery<string | undefined>({
  queryKey: ['getCompanyId'],
  queryFn: async () => {
    const { data, error } = await supabase.rpc('get_current_user_company_id');
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }
});

  // Haalt alle bedrijfsdata centraal op
  const { data: companyData, isLoading: isLoadingProjects, isError } = useCompanyData(companyId);

  const createMutation = useMutation({
    mutationFn: async (data: ProjectFormData) => {
      if (!companyId) {
        throw new Error('Geen bedrijf gevonden. Kan geen project aanmaken.');
      }
      const { error } = await supabase
        .from('projects')
        .insert({ ...data, company_id: companyId });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success('Project succesvol aangemaakt!');
      reset();
    },
    onError: (error: Error) => {
      toast.error(`Fout: ${error.message}`);
    }
  });

  const onSubmit = (data: ProjectFormData) => {
    const processedData = {
        ...data,
        end_date: data.end_date || null,
        budget: data.budget || null,
        description: data.description || null,
    };
    createMutation.mutate(processedData);
  };
  
  const isLoading = isLoadingUser || isLoadingProjects;

  // Laadscherm
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Projecten laden...</span>
        </div>
      </div>
    );
  }

  const projects = companyData?.projects || [];

  // ✅ CORRECTIE: De return statement is geherstructureerd. Het formulier is nu altijd zichtbaar.
  // De conditionele logica (fout, geen toegang, lege lijst) zit nu in het rechterpaneel.
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Projectbeheer</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Project Formulier (linkerpaneel) */}
        <div className="lg:col-span-1 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Nieuw Project Toevoegen</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Projectnaam *</label>
              <input type="text" {...register('name', { required: 'Projectnaam is verplicht' })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Locatie *</label>
              <input type="text" {...register('location', { required: 'Locatie is verplicht' })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Startdatum *</label>
              <input type="date" {...register('start_date', { required: 'Startdatum is verplicht' })} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
              {errors.start_date && <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Einddatum</label>
              <input type="date" {...register('end_date')} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select {...register('status')} defaultValue="active" className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                <option value="active">Actief</option>
                <option value="completed">Afgerond</option>
                <option value="cancelled">Geannuleerd</option>
              </select>
            </div>
            <button type="submit" disabled={!companyId || createMutation.isPending || isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md">
              {createMutation.isPending ? 'Project aanmaken...' : 'Project aanmaken'}
            </button>
            {!companyId && <p className="mt-2 text-xs text-yellow-600">Kan geen project aanmaken: geen bedrijf gekoppeld.</p>}
          </form>
        </div>

        {/* Projecten Lijst (rechterpaneel) */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Bestaande Projecten</h2>
          
          {isError ? (
            <div className="text-red-600">Fout bij het laden van projecten.</div>
          ) : !companyId ? (
            <div className="text-yellow-600">Geen bedrijf gevonden in uw account (app_metadata).</div>
          ) : projects.length > 0 ? (
            <div className="space-y-4">
              {projects.map((project: any) => (
                <div key={project.id} className="border-b dark:border-gray-700 pb-2 last:border-b-0">
                  <h3 className="font-medium text-gray-900 dark:text-white">{project.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{project.location}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
              Geen projecten gevonden. Voeg uw eerste project toe om te beginnen.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}